import type {
  LeaderboardCircuit,
  MyChronMapPoint,
  SavedTrack,
  TrackContribution,
  TrackContributionStatus,
} from '@kangaroo/shared';
import { trackContributionCornerCount } from '@kangaroo/shared';

import { downsampleTrackPoints } from './telemetrySession';
import { healCircuitPolylineForDisplay, listCatalogTrackOptions, type GpsCoord } from './trackEnrichment';

export type CircuitLayer = 'draft' | 'global';

export type CircuitListItem = {
  id: string;
  name: string;
  layoutName: string | null;
  layer: CircuitLayer;
  status: TrackContributionStatus | 'catalog';
  trackId: string;
  trackName: string | null;
  sessionKey: string | null;
  direction: 'clockwise' | 'counterclockwise' | null;
  lapLengthM: number | null;
  cornerCount: number | null;
  confidenceScore: number | null;
  updatedAt: string | null;
  address?: string | null;
  locality?: string | null;
  region?: string | null;
  countryCode?: string | null;
  centerLat?: number | null;
  centerLon?: number | null;
  source: 'contribution' | 'catalog';
  contribution: TrackContribution | null;
};

export type CircuitBrowseItem = {
  circuit: CircuitListItem;
  leaderboard: LeaderboardCircuit | null;
  center: { lat: number; lon: number } | null;
  distanceKm: number | null;
};

export type CircuitLayerFilter = 'all' | 'new' | CircuitLayer;

export function contributionLayer(status: TrackContributionStatus): CircuitLayer {
  if (status === 'merged') {
    return 'global';
  }

  return 'draft';
}

export function layerLabel(layer: CircuitLayer) {
  switch (layer) {
    case 'draft':
      return 'Draft';
    case 'global':
      return 'Global';
  }
}

export function statusLabel(status: TrackContributionStatus | 'catalog') {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'pending':
      return 'Pending review';
    case 'reviewed':
      return 'Reviewed';
    case 'accepted':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'merged':
      return 'Merged';
    case 'catalog':
      return 'Community baseline';
  }
}

export function formatCircuitDirection(direction: CircuitListItem['direction']) {
  if (!direction) {
    return '—';
  }

  return direction === 'clockwise' ? 'Clockwise' : 'Counter-clockwise';
}

export function formatCircuitDirectionShort(direction: CircuitListItem['direction']) {
  if (!direction) {
    return null;
  }

  return direction === 'clockwise' ? 'CW' : 'CCW';
}

export function formatCircuitPickerMenuLabel(
  contribution: TrackContribution,
  options: {
    distanceKm?: number | null;
    includeStatus?: boolean;
  } = {},
) {
  const { distanceKm = null, includeStatus = true } = options;

  return [
    contribution.venue?.trim() || 'Saved circuit',
    contribution.layoutName?.trim(),
    formatCircuitDirectionShort(contribution.payload.direction),
    distanceKm != null ? `${distanceKm.toFixed(1)} km` : null,
    includeStatus ? statusLabel(contribution.status) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function formatCircuitLayoutPickerLabel(
  contribution: TrackContribution,
  options: {
    distanceKm?: number | null;
    includeStatus?: boolean;
  } = {},
) {
  const { distanceKm = null, includeStatus = true } = options;

  return [
    contribution.layoutName?.trim() || 'Default layout',
    formatCircuitDirectionShort(contribution.payload.direction),
    distanceKm != null ? `${distanceKm.toFixed(1)} km` : null,
    includeStatus ? statusLabel(contribution.status) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function formatCircuitUpdatedAt(value: string | null) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function buildCircuitMapPreviewPoints(circuit: CircuitListItem, maxPoints = 120): MyChronMapPoint[] {
  const polyline = circuit.contribution?.payload.gpsPolyline;
  if (!polyline || polyline.length < 2) {
    return [];
  }

  const densified = healCircuitPolylineForDisplay(polyline as GpsCoord[], 4, 240);
  const sampled = downsampleTrackPoints(densified, maxPoints) as GpsCoord[];

  return sampled.map((coord, index) => ({
    timeMs: index * 100,
    lat: coord.lat,
    lon: coord.lon,
    speedKph: 35 + (index / Math.max(sampled.length - 1, 1)) * 55,
  }));
}

export function buildContributionCircuitItem(
  contribution: TrackContribution,
  track: string | SavedTrack | null,
): CircuitListItem {
  const trackName = typeof track === 'string' ? track : track?.name ?? null;
  const fallbackCountryCode = typeof track !== 'string' && track?.address?.length === 2
    ? track.address.toUpperCase()
    : null;
  const layoutName = contribution.layoutName
    ?.replace(/\s*[·|-]\s*[\d,.]+\s*m(?:eters?)?\s*$/i, '')
    .trim() || null;

  return {
    id: contribution.id,
    name: contribution.venue?.trim() || trackName || 'Untitled circuit',
    layoutName,
    layer: contributionLayer(contribution.status),
    status: contribution.status,
    trackId: contribution.trackId,
    trackName,
    sessionKey: contribution.sessionKey,
    direction: contribution.payload.direction,
    lapLengthM: Math.round(contribution.payload.lapLengthEstimateM),
    cornerCount: trackContributionCornerCount(contribution.payload),
    confidenceScore: contribution.payload.confidenceScore,
    updatedAt: contribution.updatedAt,
    address: contribution.location?.address ?? (typeof track !== 'string' ? track?.address : null) ?? null,
    locality: contribution.location?.locality ?? (typeof track !== 'string' ? track?.locality : null) ?? null,
    region: contribution.location?.region ?? (typeof track !== 'string' ? track?.region : null) ?? null,
    countryCode: contribution.location?.countryCode
      ?? (typeof track !== 'string' ? track?.countryCode : null)
      ?? fallbackCountryCode,
    centerLat: contribution.location?.centerLat ?? (typeof track !== 'string' ? track?.centerLat : null) ?? null,
    centerLon: contribution.location?.centerLon ?? (typeof track !== 'string' ? track?.centerLon : null) ?? null,
    source: 'contribution',
    contribution,
  };
}

export function buildCatalogCircuitItem(entry: { id: string; name: string }): CircuitListItem {
  return {
    id: `catalog-${entry.id}`,
    name: entry.name,
    layoutName: null,
    layer: 'global',
    status: 'catalog',
    trackId: entry.id,
    trackName: entry.name,
    sessionKey: null,
    direction: null,
    lapLengthM: null,
    cornerCount: null,
    confidenceScore: null,
    updatedAt: null,
    address: null,
    locality: null,
    region: null,
    countryCode: null,
    centerLat: null,
    centerLon: null,
    source: 'catalog',
    contribution: null,
  };
}

export function buildSavedTrackCircuitItem(track: SavedTrack): CircuitListItem {
  return {
    ...buildCatalogCircuitItem({ id: track.id, name: track.name }),
    id: `track-${track.id}`,
    trackId: track.id,
    trackName: track.name,
    address: track.address ?? null,
    locality: track.locality ?? null,
    region: track.region ?? null,
    countryCode: track.countryCode
      ?? (track.address?.length === 2 ? track.address.toUpperCase() : null),
    centerLat: track.centerLat,
    centerLon: track.centerLon,
    updatedAt: track.updatedAt,
  };
}

function canonicalCircuitName(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function circuitCenter(circuit: CircuitListItem) {
  if (circuit.centerLat !== null && circuit.centerLat !== undefined
    && circuit.centerLon !== null && circuit.centerLon !== undefined) {
    return { lat: circuit.centerLat, lon: circuit.centerLon };
  }
  const points = circuit.contribution?.payload.gpsPolyline ?? [];
  if (points.length === 0) {
    return null;
  }

  const totals = points.reduce(
    (sum, point) => ({ lat: sum.lat + point.lat, lon: sum.lon + point.lon }),
    { lat: 0, lon: 0 },
  );
  return { lat: totals.lat / points.length, lon: totals.lon / points.length };
}

function distanceKm(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
) {
  const earthRadiusKm = 6371;
  const radians = Math.PI / 180;
  const lat1 = from.lat * radians;
  const lat2 = to.lat * radians;
  const dLat = (to.lat - from.lat) * radians;
  const dLon = (to.lon - from.lon) * radians;
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(a));
}

export function leaderboardForCircuit(
  circuit: CircuitListItem,
  leaderboard: LeaderboardCircuit[],
) {
  const names = new Set(
    [circuit.name, circuit.trackName, circuit.layoutName]
      .filter((value): value is string => Boolean(value?.trim()))
      .map(canonicalCircuitName),
  );
  return leaderboard.find((entry) => names.has(canonicalCircuitName(entry.circuitName))) ?? null;
}

export function buildCircuitBrowseSections(
  circuits: CircuitListItem[],
  leaderboard: LeaderboardCircuit[],
  location: { lat: number; lon: number } | null,
) {
  const items: CircuitBrowseItem[] = circuits.map((circuit) => {
    const center = circuitCenter(circuit);
    return {
      circuit,
      leaderboard: leaderboardForCircuit(circuit, leaderboard),
      center,
      distanceKm: location && center ? distanceKm(location, center) : null,
    };
  });

  const used = items
    .filter((item) => item.leaderboard)
    .sort((left, right) =>
      (right.leaderboard?.lastActivityMs ?? Number.NEGATIVE_INFINITY)
      - (left.leaderboard?.lastActivityMs ?? Number.NEGATIVE_INFINITY),
    );
  const other = items
    .filter((item) => !item.leaderboard)
    .sort((left, right) => {
      if (left.distanceKm !== null && right.distanceKm !== null) {
        return left.distanceKm - right.distanceKm;
      }
      if (left.distanceKm !== null) return -1;
      if (right.distanceKm !== null) return 1;
      return left.circuit.name.localeCompare(right.circuit.name);
    });

  return { used, other };
}

export function filterCircuitItems(items: CircuitListItem[], filter: CircuitLayerFilter) {
  if (filter === 'all' || filter === 'new') {
    return filter === 'all' ? items : [];
  }

  return items.filter((item) => item.layer === filter);
}

export function summarizeCircuitLayers(items: CircuitListItem[]) {
  return {
    all: items.length,
    draft: items.filter((item) => item.layer === 'draft').length,
    global: items.filter((item) => item.layer === 'global').length,
  };
}

export function buildCircuitCatalogItems(
  teamContributions: TrackContribution[] | undefined,
  globalContributions: TrackContribution[] | undefined,
  trackById: Map<string, string | SavedTrack>,
): CircuitListItem[] {
  const catalogItems = listCatalogTrackOptions().map(buildCatalogCircuitItem);
  const savedTrackItems = [...trackById.values()]
    .filter((value): value is SavedTrack => typeof value !== 'string')
    .map(buildSavedTrackCircuitItem);
  const teamItems = (teamContributions ?? [])
    .filter((contribution) => contribution.status !== 'merged')
    .map((contribution) =>
      buildContributionCircuitItem(contribution, trackById.get(contribution.trackId) ?? null),
    );
  const globalItems = (globalContributions ?? []).map((contribution) =>
    buildContributionCircuitItem(contribution, trackById.get(contribution.trackId) ?? null),
  );

  const contributedTrackIds = new Set([...teamItems, ...globalItems].map((item) => item.trackId));
  const catalogWithoutDuplicates = [...savedTrackItems, ...catalogItems]
    .filter((item, index, items) =>
      !contributedTrackIds.has(item.trackId)
      && items.findIndex((candidate) => candidate.trackId === item.trackId) === index);

  return [...teamItems, ...globalItems, ...catalogWithoutDuplicates].sort((left, right) => {
    const leftTime = left.updatedAt ? Date.parse(left.updatedAt) : 0;
    const rightTime = right.updatedAt ? Date.parse(right.updatedAt) : 0;
    return rightTime - leftTime;
  });
}

export type CircuitTrackSort = 'top' | 'used' | 'recent' | 'nearest' | 'name';

export type CircuitTrackGroup = {
  trackId: string;
  name: string;
  configurations: CircuitBrowseItem[];
  favorite: boolean;
  address: string | null;
  locality: string | null;
  region: string | null;
  countryCode: string | null;
  center: { lat: number; lon: number } | null;
  distanceKm: number | null;
  sessionCount: number;
  lastActivityMs: number | null;
  topScore: number;
  searchText: string;
};

function countryName(code: string | null) {
  if (!code) return '';
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export function buildCircuitTrackGroups(
  circuits: CircuitListItem[],
  leaderboard: LeaderboardCircuit[],
  location: { lat: number; lon: number } | null,
  favoriteTrackIds: Iterable<string> = [],
): CircuitTrackGroup[] {
  const favorites = new Set(favoriteTrackIds);
  const grouped = new Map<string, CircuitListItem[]>();
  for (const circuit of circuits) {
    const existing = grouped.get(circuit.trackId);
    if (existing) existing.push(circuit);
    else grouped.set(circuit.trackId, [circuit]);
  }

  return [...grouped.entries()].map(([trackId, configurations]) => {
    const browseItems = buildCircuitBrowseSections(configurations, leaderboard, location);
    const items = [...browseItems.used, ...browseItems.other]
      .sort((left, right) =>
        (left.circuit.layoutName ?? left.circuit.name)
          .localeCompare(right.circuit.layoutName ?? right.circuit.name));
    const first = items[0]!.circuit;
    const center = items.find((item) => item.center)?.center ?? null;
    const uniqueSessions = new Set(
      items.flatMap((item) => item.leaderboard?.entries.map((entry) => entry.sessionKey) ?? []),
    );
    const sessionCount = Math.max(
      uniqueSessions.size,
      ...items.map((item) => item.leaderboard?.sessionCount ?? 0),
    );
    const activities = items
      .map((item) => item.leaderboard?.lastActivityMs ?? null)
      .filter((value): value is number => value !== null);
    const lastActivityMs = activities.length ? Math.max(...activities) : null;
    const recencyDays = lastActivityMs === null
      ? Number.POSITIVE_INFINITY
      : Math.max(0, (Date.now() - lastActivityMs) / 86_400_000);
    const favorite = favorites.has(trackId);
    const topScore = (favorite ? 1_000_000 : 0)
      + Math.log1p(sessionCount) * 1_000
      + (Number.isFinite(recencyDays) ? Math.max(0, 365 - recencyDays) : 0);
    const address = first.address ?? null;
    const locality = first.locality ?? null;
    const region = first.region ?? null;
    const countryCode = first.countryCode?.toUpperCase() ?? null;
    const name = first.trackName ?? first.name;
    return {
      trackId,
      name,
      configurations: items,
      favorite,
      address,
      locality,
      region,
      countryCode,
      center,
      distanceKm: location && center ? distanceKm(location, center) : null,
      sessionCount,
      lastActivityMs,
      topScore,
      searchText: [
        name,
        ...items.map((item) => item.circuit.layoutName),
        locality,
        region,
        countryCode,
        countryName(countryCode),
        address,
      ].filter(Boolean).join(' ').toLowerCase(),
    };
  });
}

export function compareCircuitTrackGroupsByUsage(
  left: CircuitTrackGroup,
  right: CircuitTrackGroup,
) {
  return right.sessionCount - left.sessionCount
    || (right.lastActivityMs ?? 0) - (left.lastActivityMs ?? 0)
    || left.name.localeCompare(right.name);
}

export function compareCircuitTrackGroupsByDistance(
  left: CircuitTrackGroup,
  right: CircuitTrackGroup,
) {
  return (left.distanceKm ?? Number.POSITIVE_INFINITY)
    - (right.distanceKm ?? Number.POSITIVE_INFINITY)
    || left.name.localeCompare(right.name);
}

export function selectMostUsedCircuitTrackGroups(
  groups: CircuitTrackGroup[],
  limit = 8,
) {
  return [...groups]
    .filter((group) => group.sessionCount > 0)
    .sort(compareCircuitTrackGroupsByUsage)
    .slice(0, limit);
}

export function sortCircuitTrackGroupsByDistance(
  groups: CircuitTrackGroup[],
) {
  return [...groups].sort(compareCircuitTrackGroupsByDistance);
}

export function filterAndSortCircuitTrackGroups(
  groups: CircuitTrackGroup[],
  options: {
    query?: string;
    countryCode?: string;
    region?: string;
    sort?: CircuitTrackSort;
  },
) {
  const query = options.query?.trim().toLowerCase() ?? '';
  const countryCode = options.countryCode?.trim().toUpperCase() ?? '';
  const region = options.region?.trim().toLowerCase() ?? '';
  const filtered = groups.filter((group) =>
    (!query || group.searchText.includes(query))
    && (!countryCode || group.countryCode === countryCode)
    && (!region || group.region?.toLowerCase() === region));
  const sort = options.sort ?? 'top';
  return filtered.sort((left, right) => {
    if (sort === 'top') return right.topScore - left.topScore || left.name.localeCompare(right.name);
    if (sort === 'used') return right.sessionCount - left.sessionCount || left.name.localeCompare(right.name);
    if (sort === 'recent') {
      return (right.lastActivityMs ?? 0) - (left.lastActivityMs ?? 0)
        || left.name.localeCompare(right.name);
    }
    if (sort === 'nearest') {
      return compareCircuitTrackGroupsByDistance(left, right);
    }
    return left.name.localeCompare(right.name);
  });
}
